import React from "react";
import { EmailComponent } from "./EmailBuilderV2";
import { ComponentEditor } from "./ComponentEditor";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronUp, ChevronDown, Copy, Trash2, Edit3 } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";

interface EmailCanvasV2Props {
  components: EmailComponent[];
  editingComponent: string | null;
  onEditComponent: (id: string | null) => void;
  onUpdateComponent: (id: string, updates: Partial<EmailComponent>) => void;
  onDeleteComponent: (id: string) => void;
  onMoveComponent: (id: string, direction: 'up' | 'down') => void;
  onDuplicateComponent: (id: string) => void;
}

export const EmailCanvasV2 = ({
  components,
  editingComponent,
  onEditComponent,
  onUpdateComponent,
  onDeleteComponent,
  onMoveComponent,
  onDuplicateComponent,
}: EmailCanvasV2Props) => {
  const renderComponent = (component: EmailComponent) => {
    switch (component.type) {
      case "header":
        return (
          <div 
            style={{
              backgroundColor: component.style.backgroundColor,
              color: component.style.color,
              padding: component.style.padding,
              textAlign: component.style.textAlign,
            }}
            className="min-h-[60px] flex items-center justify-center"
          >
            {component.content.logoUrl && (
              <img 
                src={component.content.logoUrl} 
                alt="Logo" 
                className="h-8 mr-4"
              />
            )}
            <h1 className="text-xl font-bold">
              {component.content.text || "Click to edit header"}
            </h1>
          </div>
        );

      case "text":
        return (
          <div
            style={{
              padding: component.style.padding,
              fontSize: component.style.fontSize,
              color: component.style.color,
              textAlign: component.style.textAlign,
            }}
            className="min-h-[40px]"
            dangerouslySetInnerHTML={{ 
              __html: sanitizeHtml(component.content.text || "Click to edit text...") 
            }}
          />
        );

      case "image":
        return (
          <div
            style={{
              padding: component.style.padding,
              textAlign: component.style.textAlign,
            }}
            className="min-h-[100px] flex flex-col items-center justify-center"
          >
            {component.content.url ? (
              <div className="w-full">
                <div style={{
                  display: 'flex',
                  justifyContent: component.style.imageFloat === 'left' ? 'flex-start' : 
                                component.style.imageFloat === 'right' ? 'flex-end' : 'center',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    display: 'inline-block',
                    float: component.style.imageFloat && component.style.imageFloat !== 'none' ? component.style.imageFloat : undefined
                  }}>
                    <img 
                      src={component.content.url} 
                      alt={component.content.alt || "Image"}
                      style={{
                        width: component.style.imageWidth || "auto",
                        height: component.style.imageHeight || "auto",
                        maxWidth: "100%",
                        display: 'block'
                      }}
                      className="rounded"
                      onLoad={() => console.log("Image loaded successfully:", component.content.url)}
                      onError={(e) => console.error("Image failed to load:", component.content.url, e)}
                    />
                    {component.content.caption && (
                      <p className="mt-2 text-sm text-muted-foreground" style={{
                        textAlign: component.style.imageFloat === 'left' ? 'left' : 
                                  component.style.imageFloat === 'right' ? 'right' : 'center',
                        margin: '8px 0'
                      }}>
                        {component.content.caption}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-32 bg-muted border-2 border-dashed border-muted-foreground/25 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <p>No image selected</p>
                  <p className="text-xs opacity-60">Click edit to upload</p>
                </div>
              </div>
            )}
          </div>
        );

      case "button":
        return (
          <div
            style={{
              textAlign: component.style.textAlign,
              margin: component.style.margin,
            }}
            className="min-h-[50px] flex items-center justify-center"
          >
            <button
              style={{
                backgroundColor: component.style.backgroundColor,
                color: component.style.color,
                padding: component.style.padding,
                borderRadius: component.style.borderRadius,
              }}
              className="border-none cursor-pointer"
            >
              {component.content.text || "Click to edit button"}
            </button>
          </div>
        );

      case "footer":
        return (
          <div
            style={{
              backgroundColor: component.style.backgroundColor,
              color: component.style.color,
              padding: component.style.padding,
              textAlign: component.style.textAlign,
              fontSize: component.style.fontSize,
            }}
            className="min-h-[60px]"
            dangerouslySetInnerHTML={{ 
              __html: sanitizeHtml(component.content.text || "Click to edit footer") 
            }}
          />
        );

      case "divider":
        return (
          <div className="p-2">
            <hr
              style={{
                height: component.style.height,
                backgroundColor: component.style.backgroundColor,
                border: "none",
                margin: component.style.margin,
              }}
            />
          </div>
        );

      default:
        return <div>Unknown component type</div>;
    }
  };

  if (components.length === 0) {
    return (
      <div className="h-64 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <p className="text-lg font-medium mb-2">Start Building Your Email</p>
          <p className="text-sm">Click "Add Component" above to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {components.map((component, index) => (
        <div key={component.id} className="relative group">
          {/* Component Container */}
          <div 
            className={cn(
              "border-2 transition-all duration-200 rounded-lg overflow-hidden",
              editingComponent === component.id 
                ? "border-primary ring-2 ring-primary/20" 
                : "border-transparent hover:border-muted-foreground/20"
            )}
          >
            {renderComponent(component)}
          </div>

          {/* Always Visible Controls */}
          <div className="absolute top-2 right-2 flex gap-1">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onEditComponent(component.id)}
              className="h-8 w-8 p-0 bg-white border border-border hover:bg-primary hover:text-primary-foreground shadow-md"
            >
              <Edit3 className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onMoveComponent(component.id, 'up')}
              disabled={index === 0}
              className="h-8 w-8 p-0 bg-white border border-border hover:bg-primary hover:text-primary-foreground shadow-md disabled:opacity-50"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onMoveComponent(component.id, 'down')}
              disabled={index === components.length - 1}
              className="h-8 w-8 p-0 bg-white border border-border hover:bg-primary hover:text-primary-foreground shadow-md disabled:opacity-50"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onDuplicateComponent(component.id)}
              className="h-8 w-8 p-0 bg-white border border-border hover:bg-primary hover:text-primary-foreground shadow-md"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => onDeleteComponent(component.id)}
              className="h-8 w-8 p-0 shadow-md"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Editing Indicator */}
          {editingComponent === component.id && (
            <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-1 text-xs rounded">
              Editing {component.type}
            </div>
          )}
        </div>
      ))}

      {/* Component Editor Popover */}
      {editingComponent && (
        <ComponentEditor
          component={components.find(c => c.id === editingComponent)!}
          onUpdate={(updates) => onUpdateComponent(editingComponent, updates)}
          onClose={() => onEditComponent(null)}
        />
      )}
    </div>
  );
};